import { useCallback, useEffect, useRef } from 'react';

export interface QueryLoadContext {
  requestId: number;
  isCurrentRequest: () => boolean;
}

interface UsePaginatedQueryLoaderOptions {
  enabled: boolean;
  setLoading: (isLoading: boolean) => void;
  loadPage: (context: QueryLoadContext) => Promise<void>;
  reloadKey?: string;
}

export function usePaginatedQueryLoader({
  enabled,
  setLoading,
  loadPage,
  reloadKey,
}: UsePaginatedQueryLoaderOptions) {
  const latestRequestRef = useRef(0);
  const isMountedRef = useRef(true);

  const invalidateInFlight = useCallback(() => {
    latestRequestRef.current += 1;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      invalidateInFlight();
    };
  }, [invalidateInFlight]);

  const reload = useCallback(async () => {
    const requestId = latestRequestRef.current + 1;
    latestRequestRef.current = requestId;

    const context: QueryLoadContext = {
      requestId,
      isCurrentRequest: () => latestRequestRef.current === requestId,
    };

    if (!isMountedRef.current) {
      return;
    }

    setLoading(true);
    try {
      await loadPage(context);
    } finally {
      if (context.isCurrentRequest() && isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [loadPage, setLoading]);

  useEffect(() => {
    if (!enabled) {
      invalidateInFlight();
      return;
    }

    void reload();

    return () => {
      invalidateInFlight();
    };
  }, [enabled, invalidateInFlight, reload, reloadKey]);

  return { reload };
}
